import { useState } from "react";
import { Link } from "@tanstack/react-router";
import { useRequestResetMutation } from "@/api/auth";

export function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const mutation = useRequestResetMutation();

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    mutation.mutate({ email }, { onSuccess: () => setSubmitted(true) });
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-surface">
      <div className="w-full max-w-md p-6">
        <div className="mb-8 text-center">
          <h1 className="text-3xl font-semibold tracking-tight text-ink">
            MotionForge
          </h1>
          <p className="mt-2 text-sm text-ink-3">
            Reset your password
          </p>
        </div>

        <div className="rounded-2xl border border-line bg-surface-2 p-6">
          {submitted ? (
            <div className="space-y-4">
              <h2 className="text-lg font-semibold text-ink">
                Check your email
              </h2>
              <p className="text-sm text-ink-2">
                If an account exists for{" "}
                <span className="text-accent">{email}</span>, we sent a
                password reset link. The link is valid for 1 hour.
              </p>
              <p className="text-xs text-ink-3">
                No email arriving? It may be in spam, or the address may not
                match an account on file. If you're the admin, check the API
                server log for the reset URL when SMTP isn't configured.
              </p>
              <Link
                to="/login"
                className="block text-center text-xs text-ink-2 hover:text-ink"
              >
                Back to sign in
              </Link>
            </div>
          ) : (
            <>
              <h2 className="mb-6 text-lg font-semibold text-ink">
                Forgot password
              </h2>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="mb-1 block text-sm font-medium text-ink-2">
                    Email
                  </label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    autoFocus
                    className="w-full rounded-lg border border-line bg-surface px-3 py-2 text-sm text-ink placeholder:text-ink-3 focus:border-line-strong focus:outline-none"
                  />
                </div>

                {mutation.error && (
                  <div className="rounded-lg border border-danger/30 bg-danger-soft px-3 py-2 text-xs text-danger">
                    {mutation.error.message}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={mutation.isPending}
                  className="w-full rounded-lg bg-accent py-2 text-sm font-medium text-accent-fg transition-colors hover:bg-accent-hover disabled:opacity-40"
                >
                  {mutation.isPending ? "Sending..." : "Send reset link"}
                </button>
              </form>

              <div className="mt-4 text-center">
                <Link
                  to="/login"
                  className="text-xs text-ink-2 hover:text-ink"
                >
                  Back to sign in
                </Link>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
